/**
 * Seo — declarative page metadata for React 19.
 *
 * React 19 hoists `<title>`, `<meta>`, `<link>`, and `<script type="application/ld+json">`
 * to <head> automatically when rendered in a component tree, including dedupe.
 * No HelmetProvider needed.
 *
 * Use exactly one <Seo> per route. Per-route fields override the defaults set
 * in `index.html` (title, description, canonical, og:image).
 *
 * Usage:
 *   <Seo
 *     title="Women's Ethnic Wear"
 *     description="..."
 *     canonical="/category/women"
 *     image="/og/women.jpg"
 *     jsonLd={{ '@type': 'CollectionPage', name: '...' }}
 *   />
 */

const SITE_URL = 'https://quickxkart.com';
const SITE_NAME = 'QuickXKart';
const DEFAULT_OG = `${SITE_URL}/og-image.svg`;

interface SeoProps {
  /** Page title — appended with " · QuickXKart" unless `bareTitle` is true. */
  title?: string;
  bareTitle?: boolean;
  description?: string;
  /** Absolute URL or path (will be joined with SITE_URL). */
  canonical?: string;
  /** Absolute or path to OG image. Falls back to /og-image.svg. */
  image?: string;
  /** Comma-separated keywords. */
  keywords?: string;
  /** noindex shortcut — sets robots="noindex,nofollow". */
  noIndex?: boolean;
  /** og:type — defaults to "website". Use "product" on PDP, "article" on blog. */
  ogType?: 'website' | 'product' | 'article';
  /** Inject one or more JSON-LD structured-data blocks (already-typed objects). */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

function absolutize(url: string | undefined, fallback: string): string {
  if (!url) return fallback;
  if (/^https?:\/\//i.test(url)) return url;
  const path = url.startsWith('/') ? url : `/${url}`;
  return `${SITE_URL}${path}`;
}

export function Seo({
  title,
  bareTitle,
  description,
  canonical,
  image,
  keywords,
  noIndex,
  ogType = 'website',
  jsonLd,
}: SeoProps) {
  const fullTitle = title
    ? bareTitle
      ? title
      : `${title} · ${SITE_NAME}`
    : undefined;

  const canonicalUrl = absolutize(canonical, SITE_URL);
  const ogImage = absolutize(image, DEFAULT_OG);

  const ldBlocks = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : [];

  return (
    <>
      {fullTitle && <title>{fullTitle}</title>}
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1"
        />
      )}

      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:url" content={canonicalUrl} />
      {fullTitle && <meta property="og:title" content={fullTitle} />}
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={ogImage} />

      <meta name="twitter:card" content="summary_large_image" />
      {fullTitle && <meta name="twitter:title" content={fullTitle} />}
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImage} />

      {ldBlocks.map((block, i) => (
        <script
          key={`ld-${i}`}
          type="application/ld+json"
          // JSON-LD must be inlined as a script body; React will hoist this to <head>
          // We stringify ourselves so React doesn't escape JSON-LD's quotes.
          // Safe because the content is a typed object built in app code — no user input.
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({ '@context': 'https://schema.org', ...block }),
          }}
        />
      ))}
    </>
  );
}

export const seoUtils = {
  siteUrl: SITE_URL,
  siteName: SITE_NAME,
  absolutize,
};
